// نظرة مساري — ملخص مفهوم من أول نظرة: مسارك، خطوتك التالية، برامجك، شهاداتك ومؤهلاتك، عروض مناسبة (بلا مستويات)
import { useState, useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import AdvisorPanel from "@/masar/components/AdvisorPanel";
import { useReveal, useCountUp, prefersReducedMotion } from "@/v2/lib/motion";
import heroPhoto from "@/v2/assets/official/arches_white_gold.jpg";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { GlassCard, Skeleton } from "@/v2/components/ui";
import ProfilePicker from "@/masar/components/ProfilePicker";
import { ProgramCard } from "@/masar/components/Cards";
import { CertDetails, useInterest } from "@/masar/components/CertDetails";
import { CertForm } from "@/masar/pages/Certificates";
import { useJourney } from "@/masar/lib";
import { Compass, Route, Award, Info, CheckCircle2, ArrowLeft, Hourglass, PlayCircle, ChevronLeft, Tags, BookOpen, Upload, AlertTriangle } from "lucide-react";

const Figure = ({ label, value, sub, testId, onClick }) => (
  <button onClick={onClick} className="text-start group" data-testid={testId}>
    <dt className="text-sm text-ink-3">{label}</dt>
    <dd className="m-0 text-[1.9rem] leading-tight font-semibold tabular-nums group-hover:text-beige-ink transition-colors" dir="ltr" style={{ textAlign: "right" }}>{value}</dd>
    <dd className="m-0 text-xs text-ink-3">{sub}</dd>
  </button>
);

const Shortcut = ({ icon: Icon, title, sub, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId} className="w-full text-start flex items-center gap-3 px-5 py-3.5 hover:bg-surface-2 transition-colors">
    <Icon size={18} className="text-beige-ink shrink-0" />
    <span className="flex-1 min-w-0"><span className="block font-semibold">{title}</span><span className="block text-sm text-ink-3 truncate">{sub}</span></span>
    <ChevronLeft size={16} className="text-ink-3" />
  </button>
);

const NS_ICON = { program: PlayCircle, wait: Hourglass, done: CheckCircle2, certificate: Upload, review: Award, none: Info };

export default function MasarHome() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toggle = useInterest();
  const [pendingProgram, setPendingProgram] = useState(() => sessionStorage.getItem("pending-program") || "");
  const { data, isLoading } = useJourney();
  const [changing, setChanging] = useState(false);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState(null);
  const scope = useReveal([isLoading, data?.assigned]);
  const pctRef = useCountUp(data?.indicators?.path_pct ?? 0, [isLoading]);
  const barRef = useRef(null);
  useGSAP(() => {
    if (!barRef.current || prefersReducedMotion()) return;
    gsap.from(barRef.current, { scaleX: 0, transformOrigin: "right", duration: 1.1, ease: "power2.out", delay: 0.2 });
  }, { dependencies: [isLoading, data?.indicators?.path_pct] });

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-52 w-full" /><Skeleton className="h-64 w-full" /></div>;
  if (!data?.published) return <GlassCard className="p-10 text-center text-ink-2" data-testid="masar-unpublished"><Info className="mx-auto mb-3 text-beige-ink" /> لم تُفعَّل منصة مسار بعد — ستفعّلها الإدارة قريبًا.</GlassCard>;

  if (!data.assigned || (changing && user?.profile_change_allowed)) {
    return (
      <div className="space-y-5" data-testid="masar-setup">
        <GlassCard gold className="p-6 sm:p-8 v2-pattern relative overflow-hidden">
          <div className="flex items-center gap-3 mb-2"><Compass className="text-beige-ink" /><h1 className="text-2xl sm:text-3xl font-extrabold">حدّد وظيفتك في مسار</h1></div>
          <p className="text-ink-2 text-sm max-w-2xl">{data.masar_status === "needs_completion" ? "طلب الاستكمال الخاص بك لدى الإدارة. يمكنك إعادة الاختيار إن ظهر مسمّاك الآن." : "خطوتان قصيرتان لنجهّز لك برامجك وخطواتك المناسبة."}</p>
        </GlassCard>
        <GlassCard className="p-5 sm:p-6">
          <ProfilePicker onDone={async () => { setChanging(false); await refresh(); qc.invalidateQueries({ queryKey: ["masar-journey"] }); qc.invalidateQueries({ queryKey: ["masar-me"] }); }} />
          {changing && <button onClick={() => setChanging(false)} className="text-xs text-ink-2 hover:text-ink mt-4">إلغاء</button>}
        </GlassCard>
      </div>
    );
  }

  const ind = data.indicators || {};
  const p = data.profile || {};
  const ns = data.next_step || {};
  const NsIcon = NS_ICON[ns.kind] || Info;
  const programs = data.programs || [];
  const active = programs.filter((x) => x.status !== "completed").slice(0, 3);
  const certs = [...(data.professional || []), ...(data.academic || [])].slice(0, 3);
  const goNext = () => {
    if (ns.kind === "program" && ns.challenge_id) navigate(`/journey/${ns.challenge_id}`);
    else if (ns.kind === "certificate") { const it = certs.find((c) => c.id === ns.requirement_id) || (data.professional || []).find((c) => c.id === ns.requirement_id); it ? setOpen(it) : navigate("/masar/certificates/guide"); }
    else if (ns.kind === "review") navigate("/masar/certificates");
    else navigate("/masar/plan");
  };
  const nsBtn = { program: "فتح في تميز", certificate: "التفاصيل", review: "سجل الشهادات" }[ns.kind];

  const allCerts = [...(data.professional || []), ...(data.academic || [])];
  const openFromAdvisor = (r) => {
    const it = allCerts.find((c) => c.catalog_item_id === r.id || c.id === r.id);
    it ? setOpen(it) : navigate("/masar/certificates/guide");
  };
  const pct = ind.path_pct ?? 0;
  const units = (p.units || []).map((u) => u.name).filter(Boolean);

  return (
    <div ref={scope} className="space-y-6" data-testid="masar-home">
      <header className="page-hero" data-reveal>
        <div className="page-hero-body theme-navy">
          <div className="text-[0.95rem] text-ink-2">{units.length ? units[units.length - 1] : "نظرة مساري"}</div>
          <h1>{user?.name}</h1>
          <div className="text-lg text-beige-ink" data-testid="masar-profile-title">{p.title}</div>
          <div className="mt-3 max-w-md">
            <div className="flex items-baseline justify-between gap-3 text-sm text-ink-2">
              <span>تقدم متطلبات مسارك</span>
              <span className="tabular-nums text-ink text-2xl font-semibold" dir="ltr"><span ref={pctRef}>{pct}</span>%</span>
            </div>
            <div className="h-1.5 rounded-full bg-surface-3 mt-2 overflow-hidden"><div ref={barRef} className="h-full bg-beige rounded-full" style={{ width: `${pct}%` }} /></div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {nsBtn ? <button onClick={goNext} data-testid="masar-next-step-btn" className="v2-btn">{ns.kind === "certificate" ? "ابدأ خطوتك التالية" : nsBtn} <ArrowLeft size={16} /></button>
              : <button onClick={() => navigate("/masar/plan")} data-testid="masar-goto-path" className="v2-btn"><Route size={16} /> مساري الوظيفي</button>}
            {user?.profile_change_allowed && <button onClick={() => setChanging(true)} data-testid="masar-change-profile" className="v2-btn-ghost v2-btn-sm">إعادة اختيار المسار</button>}
          </div>
        </div>
        <div className="page-hero-photo"><img src={heroPhoto} alt="أروقة المسجد النبوي" /></div>
      </header>

      {pendingProgram && (
        <div className="v2-glass v2-glass-gold p-4 flex items-center gap-3 flex-wrap" data-testid="masar-pending-program" data-reveal>
          <PlayCircle className="text-beige-ink" />
          <div className="flex-1 min-w-[200px] text-sm"><b>لديك برنامج كنت متجهًا إليه في تميز</b><div className="text-ink-2">مسارك هنا أولًا؛ يمكنك متابعة البرنامج الآن أو لاحقًا من «مساري الوظيفي».</div></div>
          <button onClick={() => { sessionStorage.removeItem("pending-program"); navigate(pendingProgram); }} className="v2-btn v2-btn-sm" data-testid="masar-pending-program-go">متابعة البرنامج <ArrowLeft size={14} /></button>
          <button onClick={() => { sessionStorage.removeItem("pending-program"); setPendingProgram(""); }} className="v2-btn-ghost v2-btn-sm" data-testid="masar-pending-program-dismiss">لاحقًا</button>
        </div>
      )}

      <div className="grid xl:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
        <AdvisorPanel onOpenGuide={openFromAdvisor} onAddCert={() => setForm({ name: "", issuer: "", kind: "professional" })} />

        <aside className="space-y-4">
          <div className="v2-glass p-5" data-testid="masar-next-step" data-reveal>
            <div className="text-sm text-ink-3 flex items-center gap-1.5"><NsIcon size={15} /> خطوتك التالية</div>
            <div className="font-semibold mt-1.5 leading-7" data-testid="masar-next-step-title">{ns.title}</div>
            {ns.desc && <div className="text-sm text-ink-2 mt-1">{ns.desc}</div>}
            {nsBtn && <button onClick={goNext} className="v2-btn-ghost v2-btn-sm mt-3">{nsBtn} <ArrowLeft size={14} /></button>}
          </div>
          <dl className="v2-glass p-5 m-0 grid grid-cols-2 gap-x-4 gap-y-5" data-testid="masar-indicators" data-reveal>
            <Figure label="شهادات مسارك" value={`${ind.certs_approved ?? 0}/${ind.certs_required ?? 0}`} sub={ind.certs_pending ? `${ind.certs_pending} قيد المراجعة` : "معتمدة"} testId="ind-certs" onClick={() => navigate("/masar/certificates/guide")} />
            <Figure label="برامجك" value={`${ind.programs_done ?? 0}/${ind.programs_total ?? 0}`} sub="مكتملة" testId="ind-programs" onClick={() => navigate("/masar/plan")} />
            <Figure label="قيد التعلّم" value={ind.programs_in_progress ?? 0} sub="في تميز" testId="ind-in-progress" onClick={() => navigate("/masar/plan")} />
            <Figure label="تقدم الدروس" value={ind.learning_pct != null ? `${ind.learning_pct}%` : "—"} sub="في الدورات" testId="ind-learning" onClick={() => navigate("/masar/plan")} />
          </dl>
          <nav className="v2-glass overflow-hidden py-1.5" aria-label="اختصارات مسار" data-reveal>
            <Shortcut icon={Award} title="سجل الشهادات" sub="شهاداتك وحالة مراجعتها" onClick={() => navigate("/masar/certificates")} testId="masar-goto-certs" />
            <Shortcut icon={BookOpen} title="دليل الشهادات" sub="كل ما يرتبط بوظيفتك" onClick={() => navigate("/masar/certificates/guide")} testId="masar-goto-guide" />
            <Shortcut icon={Tags} title="عروض التدريب" sub={data.offers_count ? `${data.offers_count} عرض ساري` : "لا عروض سارية حاليًا"} onClick={() => navigate("/masar/offers")} testId="masar-goto-offers" />
          </nav>
        </aside>
      </div>

      {data.message && !data.assigned && <div className="text-sm text-beige-ink flex items-center gap-1"><AlertTriangle size={14} /> {data.message}</div>}

      <section data-testid="masar-programs" data-reveal>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="m-0">برامجك الحالية</h2>
          <button onClick={() => navigate("/masar/plan")} className="v2-btn-ghost v2-btn-sm" data-testid="masar-all-programs">كل البرامج ({programs.length}) <ChevronLeft size={14} /></button>
        </div>
        {programs.length === 0 ? (
          <div className="v2-inset p-6 flex items-center gap-4" data-testid="masar-programs-empty"><Hourglass className="text-beige-ink shrink-0" /><div><div className="font-semibold">لا برامج مطلوبة منك حاليًا</div><p className="text-sm text-ink-2 m-0">حين تُتاح برامج تخدم مسارك ستظهر هنا مع خطوتك التالية.</p></div></div>
        ) : active.length === 0 ? (
          <div className="v2-inset p-6 flex items-center gap-4" data-testid="masar-programs-done"><CheckCircle2 className="text-ok shrink-0" /><div><div className="font-semibold">أكملت جميع برامجك</div><p className="text-sm text-ink-2 m-0">شهادات الإتمام محفوظة في سجل الشهادات.</p></div></div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">{active.map((x) => <ProgramCard key={x.program_id} p={x} compact />)}</div>
        )}
      </section>

      {open && <CertDetails item={open} onClose={() => setOpen(null)} onHave={() => { setOpen(null); setForm({ requirement_id: open.id, name: open.name, issuer: open.issuer || "", kind: open.kind }); }} />}
      {form && <CertForm initial={form} onClose={() => setForm(null)} />}
    </div>
  );
}
